/**
 * Single source of truth for supported UI locales.
 * Adding a language: append here, add `locales/<code>/*.json`, done.
 */
export const LOCALES = ['en', 'ja'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'en';

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * Pick the best supported locale from an Accept-Language header or a list of
 * browser languages. Matches on the primary subtag ("ja-JP" → "ja").
 */
export function negotiateLocale(candidates: readonly string[] | string | undefined): Locale {
  if (!candidates) return DEFAULT_LOCALE;
  const list = Array.isArray(candidates)
    ? candidates
    : String(candidates)
        .split(',')
        .map((part) => part.split(';')[0]?.trim() ?? '')
        .filter(Boolean);
  for (const candidate of list) {
    const primary = candidate.toLowerCase().split('-')[0];
    if (isLocale(primary)) return primary;
  }
  return DEFAULT_LOCALE;
}

/**
 * Namespaces map 1:1 to catalog files. Products own their own namespace so a
 * Booking-only user never downloads Invoicing strings.
 */
export const I18N_NAMESPACES = [
  'common',
  'auth',
  'account',
  'billing',
  'booking',
  'emails',
  'errors',
  'legal',
] as const;
export type I18nNamespace = (typeof I18N_NAMESPACES)[number];
